import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type {
  ContractGenerationTemplateFieldInDb,
  ContractGenerationTemplateFieldsResponse,
  ContractGenerationTemplateFieldUpdate,
} from '@/api';
import { isJsonEqual } from '@/utils/jsonHelpers';

/** What an author owns about one field — the four columns the backend stores. */
export interface FieldMetadataDraft {
  label: string | null;
  description: string | null;
  guidance: string | null;
  order_index: number | null;
}

const EMPTY_DRAFT: FieldMetadataDraft = {
  label: null,
  description: null,
  guidance: null,
  order_index: null,
};

function draftFromRow(row: ContractGenerationTemplateFieldInDb): FieldMetadataDraft {
  return {
    label: row.label ?? null,
    description: row.description ?? null,
    guidance: row.guidance ?? null,
    order_index: row.order_index ?? null,
  };
}

/**
 * What actually gets stored: trimmed text, blank meaning "nothing written"
 * (the backend refuses an empty label and treats null as cleared). The label
 * is a single line, so inner whitespace collapses too.
 */
function normalizeText(value: string | null, singleLine: boolean): string | null {
  if (value === null) return null;
  const trimmed = singleLine ? value.replace(/\s+/g, ' ').trim() : value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeDraft(value: FieldMetadataDraft): FieldMetadataDraft {
  return {
    label: normalizeText(value.label, true),
    description: normalizeText(value.description, false),
    guidance: normalizeText(value.guidance, false),
    order_index: value.order_index,
  };
}

function snapshotOf(
  response: ContractGenerationTemplateFieldsResponse
): Map<string, FieldMetadataDraft> {
  const snapshot = new Map<string, FieldMetadataDraft>();
  // Orphans get a snapshot entry too — their prose is shown, never edited, and
  // must not read as unsaved work.
  for (const row of [...response.fields, ...response.orphaned]) {
    snapshot.set(row.path, draftFromRow(row));
  }
  return snapshot;
}

export interface TemplateFieldPayload {
  fields: ContractGenerationTemplateFieldUpdate[];
  /** Edited paths no live field owns any more — the template changed under the author. */
  droppedPaths: string[];
}

/**
 * The field-guidance page's working copy: the author's edits per path beside
 * the last server state, so dirtiness is per field and a background refresh
 * can land new server rows without stepping on anything being typed. Raw text
 * stays as typed — normalization happens in the comparison and the payload —
 * so a textarea never fights the author over trailing spaces.
 */
export function useTemplateFieldDraft(): {
  draft: Ref<Map<string, FieldMetadataDraft>>;
  serverSnapshot: Ref<Map<string, FieldMetadataDraft>>;
  dirtyPaths: ComputedRef<Set<string>>;
  isDirty: ComputedRef<boolean>;
  adoptedOrphans: Ref<Map<string, string>>;
  get: (path: string) => FieldMetadataDraft;
  update: (path: string, patch: Partial<FieldMetadataDraft>) => void;
  reorderSiblings: (orderedPaths: string[]) => void;
  adoptOrphan: (orphan: ContractGenerationTemplateFieldInDb, targetPath: string) => void;
  reset: (response: ContractGenerationTemplateFieldsResponse) => void;
  absorb: (response: ContractGenerationTemplateFieldsResponse) => void;
  markSaved: (response: ContractGenerationTemplateFieldsResponse) => void;
  toPayload: (livePaths: Set<string>, renames?: Map<string, string>) => TemplateFieldPayload;
} {
  const draft = ref<Map<string, FieldMetadataDraft>>(new Map());
  const serverSnapshot = ref<Map<string, FieldMetadataDraft>>(new Map());
  /** Orphan path → the live path its notes were moved onto, this session. */
  const adoptedOrphans = ref<Map<string, string>>(new Map());

  const dirtyPaths = computed<Set<string>>(() => {
    const dirty = new Set<string>();
    for (const [path, value] of draft.value.entries()) {
      const base = serverSnapshot.value.get(path) ?? EMPTY_DRAFT;
      if (!isJsonEqual(normalizeDraft(value), normalizeDraft(base))) dirty.add(path);
    }
    return dirty;
  });

  const isDirty = computed<boolean>(() => dirtyPaths.value.size > 0);

  function get(path: string): FieldMetadataDraft {
    return draft.value.get(path) ?? EMPTY_DRAFT;
  }

  function update(path: string, patch: Partial<FieldMetadataDraft>): void {
    const next = new Map(draft.value);
    next.set(path, { ...get(path), ...patch });
    draft.value = next;
  }

  /**
   * Absolute positions for one sibling group, in the order given — the caller
   * passes exactly the group it rearranged. Nothing is re-sequenced server-side.
   */
  function reorderSiblings(orderedPaths: string[]): void {
    const next = new Map(draft.value);
    for (const [index, path] of orderedPaths.entries()) {
      next.set(path, { ...(next.get(path) ?? EMPTY_DRAFT), order_index: index });
    }
    draft.value = next;
  }

  /**
   * Move an orphan's writing onto the field that replaced it. The target keeps
   * its own position; the orphan row itself stays until its tag returns (the
   * backend never deletes authored rows), so we only remember the move.
   */
  function adoptOrphan(orphan: ContractGenerationTemplateFieldInDb, targetPath: string): void {
    update(targetPath, {
      label: orphan.label ?? null,
      description: orphan.description ?? null,
      guidance: orphan.guidance ?? null,
    });
    const next = new Map(adoptedOrphans.value);
    next.set(orphan.path, targetPath);
    adoptedOrphans.value = next;
  }

  function reset(response: ContractGenerationTemplateFieldsResponse): void {
    const snapshot = snapshotOf(response);
    serverSnapshot.value = snapshot;
    draft.value = new Map(snapshot);
    adoptedOrphans.value = new Map();
  }

  /**
   * A background refresh: adopt the server's rows, but keep every path the
   * author has touched exactly as it is. Paths the server no longer reports
   * disappear unless they are dirty — those surface at save time instead.
   */
  function absorb(response: ContractGenerationTemplateFieldsResponse): void {
    const snapshot = snapshotOf(response);
    const dirty = dirtyPaths.value;
    const next = new Map<string, FieldMetadataDraft>();
    for (const [path, value] of snapshot.entries()) {
      next.set(path, dirty.has(path) ? get(path) : value);
    }
    for (const path of dirty) {
      if (!next.has(path)) next.set(path, get(path));
    }
    serverSnapshot.value = snapshot;
    draft.value = next;
  }

  /** After a save: the response is the new baseline; "moved" markers stay for the session. */
  function markSaved(response: ContractGenerationTemplateFieldsResponse): void {
    const snapshot = snapshotOf(response);
    serverSnapshot.value = snapshot;
    draft.value = new Map(snapshot);
  }

  /**
   * The edited fields, each as its complete authored state with explicit
   * nulls — an omitted key would clear the stored value, so every column is
   * always sent.
   */
  function toPayload(
    livePaths: Set<string>,
    /** Old path → the path the field will have after this save, for tags renamed in this session. */
    renames = new Map<string, string>()
  ): TemplateFieldPayload {
    const fields: ContractGenerationTemplateFieldUpdate[] = [];
    const droppedPaths: string[] = [];
    for (const path of dirtyPaths.value) {
      const target = renames.get(path) ?? path;
      if (!livePaths.has(target)) {
        droppedPaths.push(path);
        continue;
      }
      const value = normalizeDraft(get(path));
      fields.push({
        path: target,
        label: value.label,
        description: value.description,
        guidance: value.guidance,
        order_index: value.order_index,
      });
    }
    return { fields, droppedPaths };
  }

  return {
    draft,
    serverSnapshot,
    dirtyPaths,
    isDirty,
    adoptedOrphans,
    get,
    update,
    reorderSiblings,
    adoptOrphan,
    reset,
    absorb,
    markSaved,
    toPayload,
  };
}
